/**
 * The ROLE of each configurable field of a descriptor (genericity plan
 * §2 bis, 2026-10-24): what it sets in its component — the CPU reserved by
 * the app, the size of a volume, the minimum of the autoscaling… — so that
 * the generic Configuration tab (step 5) and the promotion summary (step 6)
 * can place it without code per type.
 *
 * RESOLUTION, per configurable field:
 *  1. `components[].resources` (source `resources`): the field a
 *     component's `cpuRequest`, `cpuLimit`, `memoryRequest` or
 *     `memoryLimit` names has that role IN THAT COMPONENT — in each of them
 *     when several name it (one « CPU des services » for two components);
 *  2. otherwise the contract's table (`PATH_ROLES`, source `path`) on the
 *     path after the `valuesPath` of the component with the LONGEST such
 *     prefix — when that component is the field's own; `storage.size` only
 *     on a cnpg or mariadb component; the types `databaseMode` and
 *     `databaseSync` are their own role;
 *  3. `field.role` (source `declared`), for a wrapper chart whose paths
 *     differ from the contract's: it only fills a missing role — another
 *     role than the one 1 or 2 gives is refused, the same one is accepted;
 *  4. otherwise no role: the tab's « Autres réglages ».
 *
 * NO NEW REFUSAL WITHOUT A NEW KEY: a descriptor that compiled before this
 * step still compiles. A role from 1 or 2 that does not fit (type, unit,
 * component kind) is simply not given — the field goes to « Autres
 * réglages » —, nor one a former field of the component already holds. The
 * refusals concern the new keys: a declared role of the wrong type, unit or
 * component kind, held by another field of its component (`volume`
 * excepted), or an HPA minimum or maximum declared without the other;
 * `mountedBy` off a volume or naming an unknown component.
 *
 * A request without its limit is never refused: a key absent from
 * `components[].resources` is the LimitRange's default, a constant one is
 * fixed — both count in the quota (`environmentQuota.ts`,
 * `componentContainer`).
 */

import type {
  DescriptorComponent,
  DescriptorField,
  FieldUnit,
} from './descriptor';

export const FIELD_ROLES = [
  'cpuRequest',
  'cpuLimit',
  'memoryRequest',
  'memoryLimit',
  'replicas',
  'hpaEnabled',
  'hpaMin',
  'hpaMax',
  'hpaTargetCpu',
  'hpaScaleDownWindow',
  'volume',
  'enabled',
  'databaseMode',
  'databaseSync',
  'databaseSize',
] as const;
export type FieldRole = (typeof FIELD_ROLES)[number];
/** The roles a component has at most one field of: all but `volume`. */
export type SingleFieldRole = Exclude<FieldRole, 'volume'>;
export type RoleSource = 'resources' | 'path' | 'declared';

/** How a configurable field was placed (`role` and `roleSource` together). */
export interface FieldRoleBinding {
  field: string;
  /** Its component (`field.component`). */
  component: string;
  role?: FieldRole;
  roleSource?: RoleSource;
  /** `field.mountedBy` (a shared volume's other components), else empty. */
  mountedBy: string[];
}

/** The fields of one component by role: what a section of the tab shows. */
export interface ComponentRoles {
  component: string;
  /** Role → field id. */
  roles: Partial<Record<SingleFieldRole, string>>;
  /** The fields of role `volume`, in the descriptor's order. */
  volumes: string[];
}

export interface DescriptorRoles {
  /** Every configurable field, by id, in the descriptor's order. */
  fieldRoles: ReadonlyMap<string, FieldRoleBinding>;
  /** Every component, by id, in the descriptor's order (empty when no field). */
  componentRoles: ReadonlyMap<string, ComponentRoles>;
  /** The configurable fields without a role, in order: « Autres réglages ». */
  unclassifiedFields: string[];
}

/** The field as written: what `DescriptorField` does not keep. */
export interface RawFieldRole {
  role?: FieldRole;
  mountedBy?: string[];
}

const RESOURCE_ROLES = [
  'cpuRequest',
  'cpuLimit',
  'memoryRequest',
  'memoryLimit',
] as const;
type ResourceRole = (typeof RESOURCE_ROLES)[number];

function isResourceRole(role: FieldRole): role is ResourceRole {
  return (RESOURCE_ROLES as readonly FieldRole[]).includes(role);
}

/** The contract's paths (after the component's `valuesPath`) and their role. */
const PATH_ROLES: ReadonlyMap<string, FieldRole> = new Map([
  ['replicaCount', 'replicas'],
  ['autoscaling.enabled', 'hpaEnabled'],
  ['autoscaling.minReplicas', 'hpaMin'],
  ['autoscaling.maxReplicas', 'hpaMax'],
  ['autoscaling.targetCPUUtilizationPercentage', 'hpaTargetCpu'],
  ['autoscaling.scaleDownStabilizationWindowSeconds', 'hpaScaleDownWindow'],
  ['persistence.size', 'volume'],
  ['enabled', 'enabled'],
]);

type Shape =
  | { type: 'quantity'; unit: FieldUnit }
  | { type: 'integer'; units: ReadonlyArray<FieldUnit | undefined> }
  | { type: 'boolean' | 'databaseMode' | 'databaseSync' };

const SHAPES: Readonly<Record<FieldRole, Shape>> = {
  cpuRequest: { type: 'quantity', unit: 'm' },
  cpuLimit: { type: 'quantity', unit: 'm' },
  memoryRequest: { type: 'quantity', unit: 'Mi' },
  memoryLimit: { type: 'quantity', unit: 'Mi' },
  replicas: { type: 'integer', units: [undefined] },
  hpaEnabled: { type: 'boolean' },
  hpaMin: { type: 'integer', units: [undefined] },
  hpaMax: { type: 'integer', units: [undefined] },
  hpaTargetCpu: { type: 'integer', units: [undefined, '%'] },
  hpaScaleDownWindow: { type: 'integer', units: [undefined, 's'] },
  volume: { type: 'quantity', unit: 'Gi' },
  enabled: { type: 'boolean' },
  databaseMode: { type: 'databaseMode' },
  databaseSync: { type: 'databaseSync' },
  databaseSize: { type: 'quantity', unit: 'Gi' },
};

/** The component kinds a role is allowed on (absent: every kind). */
const ROLE_KINDS: Partial<
  Record<FieldRole, ReadonlyArray<DescriptorComponent['kind']>>
> = {
  replicas: ['app'],
  hpaEnabled: ['app'],
  hpaMin: ['app'],
  hpaMax: ['app'],
  hpaTargetCpu: ['app'],
  hpaScaleDownWindow: ['app'],
  databaseMode: ['cnpg'],
  databaseSync: ['cnpg'],
  databaseSize: ['cnpg', 'mariadb'],
};

function isPrefix(prefix: string[], path: string[]): boolean {
  return (
    prefix.length < path.length &&
    prefix.every((segment, index) => path[index] === segment)
  );
}

/**
 * The roles of `fields` (`raw[i]` as written for `fields[i]`), every
 * problem passed to `fail`.
 */
export function resolveFieldRoles(
  components: DescriptorComponent[],
  fields: DescriptorField[],
  raw: RawFieldRole[],
  fail: (message: string) => void,
): DescriptorRoles {
  // Each component with its roles' entry, in the descriptor's order.
  const componentById = new Map(
    components.map(item => {
      const entry: ComponentRoles = {
        component: item.id,
        roles: {},
        volumes: [],
      };
      const sources = new Map<SingleFieldRole, RoleSource>();
      return [item.id, { component: item, entry, sources }] as const;
    }),
  );
  type Owner = NonNullable<ReturnType<typeof componentById.get>>;

  /** Gives `role` to `field` in `owner`; false if another field holds it. */
  const assign = (
    owner: Owner,
    field: string,
    role: FieldRole,
    source: RoleSource,
  ): boolean => {
    if (role === 'volume') {
      owner.entry.volumes.push(field);
      return true;
    }
    const holder = owner.entry.roles[role];
    if (holder !== undefined && holder !== field) {
      if (source === 'declared' || owner.sources.get(role) === 'declared') {
        fail(
          `composant ${owner.component.id} : rôle ${role} porté par plusieurs champs (${holder}, ${field})`,
        );
      }
      return false;
    }
    owner.entry.roles[role] = field;
    owner.sources.set(role, source);
    return true;
  };

  // 1. components[].resources: field id → the components naming it.
  const configurable = new Set(
    fields.filter(field => field.configurable).map(field => field.id),
  );
  const listings = new Map<
    string,
    Array<{ owner: Owner; role: ResourceRole }>
  >();
  for (const owner of componentById.values()) {
    for (const key of RESOURCE_ROLES) {
      const ref = owner.component.resources[key];
      if (typeof ref !== 'string' || !configurable.has(ref)) continue;
      listings.set(ref, [...(listings.get(ref) ?? []), { owner, role: key }]);
    }
  }

  const fieldRoles = new Map<string, FieldRoleBinding>();
  const unclassifiedFields: string[] = [];

  fields.forEach((field, index) => {
    const where = `champ ${field.id}`;
    const declared = raw[index]?.role;
    const mountedBy = raw[index]?.mountedBy ?? [];
    if (!field.configurable) {
      if (declared !== undefined) {
        fail(`${where} : rôle ${declared} sur un champ non configurable`);
      }
      if (mountedBy.length > 0) {
        fail(`${where} : mountedBy sur un champ non configurable`);
      }
      return;
    }
    const own = componentById.get(field.component);
    const listed = listings.get(field.id) ?? [];
    const fits = (role: FieldRole, owner: Owner) =>
      roleProblems(where, field, role, owner.component, false).length === 0;
    let role: FieldRole | undefined;
    let roleSource: RoleSource | undefined;
    let component = own?.component.id ?? listed[0]?.owner.component.id;

    if (listed.length > 0) {
      if (declared !== undefined && !listed.some(l => l.role === declared)) {
        fail(
          `${where} : rôle déclaré ${declared}, contredit par components[].resources (${listed
            .map(l => `${l.owner.component.id}.${l.role}`)
            .join(', ')})`,
        );
      }
      // Its own component's listing first, then the descriptor's order.
      const ordered = [
        ...listed.filter(l => l.owner === own),
        ...listed.filter(l => l.owner !== own),
      ];
      for (const listing of ordered) {
        const source = declared === listing.role ? 'declared' : 'resources';
        if (source === 'declared') {
          // A request in another unit: refused by the per-pod guard
          // already (descriptor.ts, « doit être en »).
          for (const problem of roleProblems(
            where,
            field,
            listing.role,
            listing.owner.component,
            true,
          )) {
            fail(problem);
          }
        } else if (!fits(listing.role, listing.owner)) {
          continue;
        }
        if (
          assign(listing.owner, field.id, listing.role, source) &&
          role === undefined
        ) {
          role = listing.role;
          roleSource = source;
          component = own ? own.component.id : listing.owner.component.id;
        }
      }
    } else if (own) {
      const deduced = deducedRole(field, components, own.component);
      const deducedFits = deduced !== undefined && fits(deduced, own);
      if (declared !== undefined) {
        if (deducedFits && deduced !== declared) {
          fail(
            `${where} : rôle déclaré ${declared}, contredit le rôle déduit de son chemin (${deduced})`,
          );
        } else if (isResourceRole(declared)) {
          fail(
            `${where} : rôle ${declared} sans components[].resources.${declared} = ${field.id} dans le composant ${field.component} (le quota ne le compterait pas)`,
          );
        }
        for (const problem of roleProblems(
          where,
          field,
          declared,
          own.component,
          false,
        )) {
          fail(problem);
        }
        if (assign(own, field.id, declared, 'declared')) {
          role = declared;
          roleSource = 'declared';
        }
      } else if (
        deducedFits &&
        deduced !== undefined &&
        assign(own, field.id, deduced, 'path')
      ) {
        role = deduced;
        roleSource = 'path';
      }
    }
    if (role === undefined) {
      unclassifiedFields.push(field.id);
    }

    if (mountedBy.length > 0 && role !== 'volume') {
      fail(`${where} : mountedBy n’est permis que sur un volume (rôle volume)`);
    }
    for (const id of mountedBy) {
      if (!componentById.has(id)) {
        fail(`${where} mountedBy : composant inconnu ${id}`);
      } else if (id === field.component) {
        fail(`${where} mountedBy : ${id} est déjà le composant du champ`);
      }
    }

    fieldRoles.set(field.id, {
      field: field.id,
      component: component ?? field.component,
      ...(role !== undefined && roleSource !== undefined
        ? { role, roleSource }
        : {}),
      mountedBy,
    });
  });

  // The HPA bounds go together — refused only when one is declared.
  for (const { entry, sources } of componentById.values()) {
    for (const [has, lacks] of [
      ['hpaMin', 'hpaMax'],
      ['hpaMax', 'hpaMin'],
    ] as const) {
      if (
        entry.roles[has] !== undefined &&
        entry.roles[lacks] === undefined &&
        sources.get(has) === 'declared'
      ) {
        fail(`composant ${entry.component} : rôle ${has} sans rôle ${lacks}`);
      }
    }
  }

  const componentRoles = new Map(
    [...componentById].map(([id, { entry }]) => [id, entry]),
  );
  return { fieldRoles, componentRoles, unclassifiedFields };
}

/** Rule 3: the contract's path, or the CloudNativePG types. */
function deducedRole(
  field: DescriptorField,
  components: DescriptorComponent[],
  own: DescriptorComponent,
): FieldRole | undefined {
  if (field.type === 'databaseMode' || field.type === 'databaseSync') {
    return field.type;
  }
  const holder = components
    .filter(item => isPrefix(item.valuesPath, field.path))
    .reduce<DescriptorComponent | undefined>(
      (best, item) =>
        !best || item.valuesPath.length > best.valuesPath.length ? item : best,
      undefined,
    );
  if (holder !== own) {
    return undefined;
  }
  const suffix = field.path.slice(own.valuesPath.length).join('.');
  if (suffix === 'storage.size') {
    return own.kind === 'cnpg' || own.kind === 'mariadb'
      ? 'databaseSize'
      : undefined;
  }
  return PATH_ROLES.get(suffix);
}

/**
 * What makes `role` unfit for `field` of `component` (type, unit, kind);
 * `guarded`: a request's unit, which the per-pod guard checks, left out.
 */
function roleProblems(
  where: string,
  field: DescriptorField,
  role: FieldRole,
  component: DescriptorComponent,
  guarded: boolean,
): string[] {
  const problems: string[] = [];
  const shape = SHAPES[role];
  if (field.type !== shape.type) {
    problems.push(`${where} : le rôle ${role} exige le type ${shape.type}`);
  } else if (shape.type === 'quantity' && field.unit !== shape.unit) {
    if (!(guarded && (role === 'cpuRequest' || role === 'memoryRequest'))) {
      problems.push(
        `${where} : le rôle ${role} exige une quantité en ${shape.unit}`,
      );
    }
  } else if (shape.type === 'integer' && !shape.units.includes(field.unit)) {
    const units = shape.units.filter(unit => unit !== undefined);
    problems.push(
      `${where} : le rôle ${role} exige un entier ${
        units.length > 0 ? `sans unité ou en ${units.join(', ')}` : 'sans unité'
      }`,
    );
  }
  const kinds = ROLE_KINDS[role];
  if (kinds && !kinds.includes(component.kind)) {
    problems.push(
      component.kind === 'mariadb'
        ? `${where} : rôle ${role} sur le composant mariadb ${component.id}, une instance sans haute disponibilité`
        : `${where} : le rôle ${role} n’est permis que sur un composant ${kinds.join(
            ' ou ',
          )}`,
    );
  }
  return problems;
}
